import { HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal, effect } from "@angular/core";
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, of, tap } from "rxjs";
import { EzUserSetting, IUserSettings, SettingsConfig } from "@template-ui/types/user-setting.model";
import { RoleService } from "./roles.service";
import { AppAngularBaseClass } from "@app/core/services/app-angular-base";

type CurrentUserSettingDto = {
  key?: string;
  value?: string;
  settingName?: string;
  settingValue?: string;
};

@Injectable({
  providedIn: "root"
})
export class SettingsService extends AppAngularBaseClass implements IUserSettings {  // Can't extend any base classes here because they all include settings

  /*
    NOTE: Classic BehaviorSubject + Observable pattern here. In TypeScript, there is
    no need to use .asObservable because a BehaviorSubject is already an Observable.
    It's not a big deal either way, so just testing out the casting method here.
  */
  private userSettingsSubject = new BehaviorSubject<EzUserSetting[]>([]);

  /**
   * UserSettings observable (using a cast). Subscribe to this (using get$) if you want updates whenever a user
   * setting is changed.
   */
  private userSettings$: Observable<EzUserSetting[]> = this.userSettingsSubject; // this.userSettingsSubject.asObservable();

  roleService = inject(RoleService);

  private readonly settingsHttpOptions = {
    headers: new HttpHeaders({
      "skip-error-interceptor": "yes",
      "skip-loading-interceptor": "yes",
    }),
    withCredentials: true,
  };

  userSettings = signal<EzUserSetting[]>([]);

  // toggleDlogSubject = new BehaviorSubject<boolean>(false);
  // toggleDlog$ = this.toggleDlogSubject.asObservable();

  dlogEnabledSig = signal<boolean>(false);
  roles: string[] = [];

  constructor( ) {
    super();
    // Keep the BehaviorSubject in sync with the signal
    // TODO: Figure out if this is needed and if so, fix it.
    // effect(() => {
    //   this.userSettingsSubject.next(this.userSettings() ?? []);  // This causes infinite loop (not the dashboard panel flashing though)
    // });

    // // Trigger initial load
    effect(() => {
      // Using the access profile as the trigger prevents anonymous startup calls.
      const accessProfileState = this.roleService.accessProfileState();
      this.roles = this.roleService.roles();
      if (accessProfileState === "loaded" || this.roles.length > 0) {
        this.refresh();
      }
    })

    // Uncomment and adjust if you want initial load on auth
    // this.oidcService.isAuthenticated$.pipe(this.takeUntilDestroyed()).subscribe((data) => {
    //   if (data.isAuthenticated) {
    //     this.refresh();
    //   }
    // });
  }


  /**
   * Get a single user setting by its key
   * @param key
   * @returns an AppUserSetting or undefined if not found
   */
  get(key: string): EzUserSetting | undefined {
    //return this.userSettingsSubject.value.find((x) => x.settingName === key);
    return this.userSettings().find((x) => x.settingName === key);
  }

  getFromLocalStorage(key: string): string {
    return localStorage.getItem(key) ?? "";
  }

  /**
   * Get the userSettings observable. Subscribe to this to get the current
   * AppUserSettings and get notified whenever they change
   * @returns Observable<AppUserSetting[]>
   */
  // get$(): Observable<EzUserSetting[]> {
  //   return this.userSettings$;
  // }

  /**
   * Get an observable that will only fire when the specified
   * setting changes.
   * @param key - the setting key in the database.
   * @returns an observable that will only change when the specified setting changes
   */
  getSetting$(key: string): Observable<EzUserSetting | undefined> {
    return this.userSettings$.pipe(
      map((settings) => settings.find((x) => x.settingName === key)), // Extract the specific setting value
      distinctUntilChanged() // Prevent unnecessary emissions for unchanged values
    );
  }

  // getSetting$(key: string): Observable<EzUserSetting | undefined> {
  //   return toObservable(this.userSettings).pipe(
  //     map(settings => settings?.find(x => x.settingName === key)),
  //     distinctUntilChanged()
  //   );
  // }

  /**
   * Compare two AppUserSettings and return true if they are equal (enough)
   * @param a
   * @param b
   */
  areEqual(a: EzUserSetting, b: EzUserSetting): boolean {
    return a.settingName == b.settingName && a.settingValue == b.settingValue; // The rest doesn't matter
  }

  /**
   * Save the provided AppUserSetting to both local storage
   * and the databse for the current user.
   * @param key the setting key
   * @param setting an AppUserSetting or an already serialized AppUserSetting
   */
  saveSettingToBoth(key?: string, setting?: EzUserSetting | string) {
    if (!key) {
      console.warn(`Tried to save setting without all required parameters. Key=[${key}], Setting=[${JSON.stringify(setting)}]`);
      return;
    }
    let value: string;
    if (typeof setting === "string") {
      value = setting;
    } else {
      value = JSON.stringify(setting);
    }

    this.saveToLocalStorage(key, value);
    this.saveToDb(key, value);
  }

  saveToDb(key: string, value: string) {
    const normalizedKey = key?.trim();
    if (!normalizedKey) {
      return;
    }

    this.http
      .post<CurrentUserSettingDto>(
        this.apis.core.manual.postCurrentUserSettings(),
        { key: normalizedKey, value },
        this.settingsHttpOptions,
      )
      .pipe(
        catchError(() => of(undefined)),
        this.takeUntilDestroyed(),
      )
      .subscribe((setting) => {
        if (setting) {
          this.upsertSetting(this.mapDtoToSetting(setting));
        }
      });
  }

  saveToLocalStorage(key: string, value: string) {
    const setting = this.get(key); //TODO: this doesn't really make senese, it should be this.getFromLocalStorage(key)
    if (setting) {
      setting.settingValue = value;
      localStorage.setItem(key, setting.settingValue);
    } else {
      const newSetting = {
        settingName: key,
        settingValue: value
      };
      localStorage.setItem(key, newSetting.settingValue);
    }
  }

  /**
   * Implementation of IUserSettings.refresh
   * Get the settings from the database and set the signal.
   */
  refresh(): void {
    if((this.roles && this.roles.length > 0) || this.roleService.accessProfileState() === "loaded")
    {
      this.http
        .get<CurrentUserSettingDto[]>(
          this.apis.core.manual.getCurrentUserSettings(),
          this.settingsHttpOptions,
        )
        .pipe(
          map((settings) => settings.map((setting) => this.mapDtoToSetting(setting))),
          tap((settings) => this.setUserSettings(settings)),
          catchError(() => {
            this.setUserSettings([]);
            return of([]);
          }),
          this.takeUntilDestroyed(),
        )
        .subscribe();
    }
  }

  /**
   * Implementation of IUserSettings.saveSetting
   * @param keyOrConfig - either a string key or a SettingsConfig
   * @param value - A string to be saved
   */
  saveSetting(keyOrConfig: string | SettingsConfig, value: string): void {

    if(typeof(keyOrConfig) !== 'string')
    {
      const key = keyOrConfig.settingsKey;
      const loc = keyOrConfig.settingsLocation;
      if(loc)
      {
        switch(loc)
        {
          case "db":
            this.saveToDb(key, value);
            break;
          case "local":
            this.saveToLocalStorage(key, value);
            break;
          case "both":
            this.saveSettingToBoth(key, value);
            break;
        }
      }
      else
      {
        // config provided without location, assume db
        this.saveToDb(key, value);
      }
    }
    else
    {
      // Only a key provided, assume db.
      this.saveToDb(keyOrConfig, value);
    }
  }

  removeFromLocalStorage(key: string) {
    localStorage.removeItem(key);
  }

  toggleDlog(event: boolean) {
    //this.toggleDlogSubject.next(event);
    this.dlogEnabledSig.set(event);
    //this.saveToLocalStorage("enableDlog", event.toString()); // I'm ok turning it on each time.
  }

  /**
   * Since there is no clearAll db endpoint, loop through all the known settings
   * and set them to blank.
   */
  clearAllUserSettingsFromDatabase() {
    this.userSettings().forEach((setting: EzUserSetting) => {

      if(setting.settingName)
      {
        this.saveToDb(setting.settingName, "");
      }
    });

    setTimeout(() => {
      this.roleService.reload();
    }, 1000);

  }

  // ngOnDestroy(): void {
  //   this.destroy$.next(true);
  // }

  private mapDtoToSetting(setting: CurrentUserSettingDto): EzUserSetting {
    return new EzUserSetting(
      setting.key ?? setting.settingName ?? "",
      setting.value ?? setting.settingValue ?? "",
    );
  }

  private setUserSettings(settings: EzUserSetting[]): void {
    this.userSettings.set(settings);
    this.userSettingsSubject.next(settings);
  }

  private upsertSetting(setting: EzUserSetting): void {
    if (!setting.settingName) {
      return;
    }

    const settings = [...this.userSettings()];
    const existingIndex = settings.findIndex((item) => item.settingName === setting.settingName);
    if (existingIndex >= 0) {
      settings[existingIndex] = setting;
    } else {
      settings.push(setting);
    }

    this.setUserSettings(settings);
  }
}
